import { useContext, useMemo, useState } from "react";
import { Modal, Box, CircularProgress } from "@mui/material";
import { useSnackbar } from "../../../hooks/SnackbarProvider";
import "./CartCardViewLayout.css";
import { UserContext } from "../../../../../../App";
import vegIcon from "../../../../../../assets/images/veg-new.png";
import nonVegIcon from "../../../../../../assets/images/non-veg-icon.svg";

const isItemAvailable = (itemAvailabilities) => {
  if (!itemAvailabilities || itemAvailabilities.length === 0) return true;
  const now = new Date();
  const cur = now.getHours() * 60 + now.getMinutes();
  return itemAvailabilities.some(({ startTime, endTime }) => {
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);
    return cur >= sh * 60 + sm && cur <= eh * 60 + em;
  });
};

const getNotServingLabel = (itemAvailabilities) => {
  if (!itemAvailabilities?.length) return "Not serving now";
  const slot = itemAvailabilities[0];
  if (!slot?.endTime) return "Not serving now";
  const fmt = (t) => {
    const [h, m] = t.split(":").map(Number);
    const ap = h >= 12 ? "PM" : "AM";
    return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ap}`;
  };
  return `Not serving now · Breakfast menu until ${fmt(slot.endTime)}`;
};

const formatAvailabilityTimes = (itemAvailabilities) => {
  if (!itemAvailabilities || itemAvailabilities.length === 0) return "";
  return itemAvailabilities.map(({ startTime, endTime }) => `${startTime} - ${endTime}`).join(", ");
};

const getRestrictOosConfig = () => {
  try {
    const appConfig = JSON.parse(localStorage.getItem("appConfig") || "{}");
    const flag = appConfig?.all?.RESTRICT_OOS_ORDERING?.flag;
    return flag === false ? false : true; // default true
  } catch {
    return true;
  }
};

const CartCardViewLayout = ({
  layoutType,
  color,
  title,
  price,
  img,
  alt,
  onClick,
  onTileClick,
  loading,
  shortCode,
  foodType,
  complimentaryMode,
  isBundle,
  bundleVariants,
  itemAvailabilities,
  hasMultipleVariants,
  isOutOfStock,
  saleMode,
}) => {
  const showSnackbar = useSnackbar();
  const { color: themeColor } = useContext(UserContext);
  const retailType = localStorage.getItem("retailType");

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const isAvailable = useMemo(() => isItemAvailable(itemAvailabilities), [itemAvailabilities]);
  const notServingLabel = useMemo(
    () => getNotServingLabel(itemAvailabilities),
    [itemAvailabilities]
  );
  const availabilityText = useMemo(
    () => formatAvailabilityTimes(itemAvailabilities),
    [itemAvailabilities]
  );
  const restrictOos = useMemo(() => getRestrictOosConfig(), []);

  const isUnavailable = isOutOfStock || !isAvailable;
  // Button is disabled only for out-of-stock when restriction is ON
  // (not-serving-now items still show the Add button → opens confirmation modal)
  const isButtonDisabled = isOutOfStock && restrictOos;

  const handleButtonClick = (e) => {
    e?.stopPropagation();
    if (complimentaryMode) {
      showSnackbar("Please disable the complimentary mode", "warning");
      return;
    }
    const cartId = localStorage.getItem("CartId");
    if (!cartId) {
      showSnackbar("Please 'ADD' or 'SKIP' customer to add products", "error");
      return;
    }
    // If restriction is ON, block OOS/unavailable
    if (restrictOos) {
      if (isOutOfStock) {
        showSnackbar("This item is currently out of stock", "warning");
        return;
      }
      if (!isAvailable) {
        setShowConfirmModal(true);
        return;
      }
    }
    onClick();
  };

  const foodIconMap = {
    VEG: vegIcon,
    NON_VEG: nonVegIcon,
  };
  const foodIcon = foodIconMap[foodType];

  const currentColor = color || themeColor;

  return (
    <>
      <div
        className={`productListCard-CardLayout ${currentColor === "dark" ? "dark" : ""}${
          isButtonDisabled && !onTileClick ? " ccl-card-disabled" : ""
        }`}
        onClick={onTileClick ?? (isButtonDisabled ? undefined : handleButtonClick)}
        style={{ cursor: isButtonDisabled && !onTileClick ? "not-allowed" : "pointer" }}
      >
        {/* Image section */}
        <section className="CardLayout-image-section">
          {loading && (
            <div className="ccl-img-loader">
              <CircularProgress size={20} sx={{ color: "#0860E6" }} />
            </div>
          )}
          <img
            src={img}
            alt={alt || "Product"}
            width="100%"
            height="100%"
            loading="lazy"
            style={{ objectFit: "cover", display: "block" }}
          />

          {/* White pill overlay for OOS / not available */}
          {isUnavailable && (
            <div className="ccl-status-overlay">
              <span className="ccl-status-pill">
                {isOutOfStock ? "Out of stock" : notServingLabel}
              </span>
            </div>
          )}
        </section>

        {/* Title section */}
        <section className="CardLayout-title-section">
          <div className="CardLayout-title-logo-row-container">
            <div className="CardLayout-title-logo-row">
              <p className="CardLayout-title">{title || "-"}</p>
              {retailType === "RESTAURANT" && foodIcon && (
                <img src={foodIcon} alt={foodType} title={foodType} className="ccl-food-icon" />
              )}
            </div>

            {shortCode && <p className="CardLayout-short-code">{shortCode}</p>}

            {isBundle && bundleVariants?.length > 0 && (
              <p className="ccl-includes-text">Includes {bundleVariants.length} items</p>
            )}
          </div>
        </section>

        {/* Price + action section */}
        <section className="CardLayout-mrp-section">
          {hasMultipleVariants ? (
            <p className="CardLayout-variants">More Variants</p>
          ) : (
            <p className="CardLayout-price">
              {price !== null && price !== undefined && price !== "" ? (typeof price === "number" ? `₹${price}` : price) : ""}
            </p>
          )}

          <div onClick={(e) => e.stopPropagation()}>
            {isButtonDisabled ? (
              <span className="ccl-unavailable-text">Unavailable</span>
            ) : isBundle ? (
              <button
                className="ccl-action-btn"
                onClick={handleButtonClick}
                onKeyDown={(e) => {
                  if (e.repeat && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                  }
                }}
              >
                View Combo
              </button>
            ) : (
              <button
                className="ccl-action-btn"
                onClick={handleButtonClick}
                onKeyDown={(e) => {
                  if (e.repeat && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                  }
                }}
              >
                Add +
              </button>
            )}
          </div>
        </section>
      </div>

      {/* Confirmation Modal for not-available items */}
      <Modal open={showConfirmModal} onClose={() => setShowConfirmModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            maxWidth: "90%",
            bgcolor: currentColor === "dark" ? "#353a50" : "white",
            color: currentColor === "dark" ? "white" : "black",
            boxShadow: 24,
            borderRadius: "8px",
            p: 3,
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: "1rem", fontSize: "1.25rem" }}>
            Item Not Available
          </h3>
          <p style={{ marginBottom: "0.5rem", fontSize: "0.938rem" }}>
            <strong>{title}</strong> is not available at this time.
          </p>
          <p style={{ marginBottom: "1.5rem", fontSize: "0.875rem", color: "#ff3b30" }}>
            Available during: {availabilityText}
          </p>
          <p style={{ marginBottom: "1.5rem", fontSize: "0.938rem" }}>
            Do you want to proceed adding this product to the cart?
          </p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
            <button
              onClick={() => setShowConfirmModal(false)}
              style={{
                padding: "10px 20px",
                backgroundColor: currentColor === "dark" ? "#48484a" : "#e0e0e0",
                color: currentColor === "dark" ? "white" : "black",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "0.938rem",
                fontWeight: 600,
              }}
            >
              No
            </button>
            <button
              onClick={() => {
                setShowConfirmModal(false);
                onClick();
              }}
              style={{
                padding: "10px 20px",
                backgroundColor: "#0860E6",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "0.938rem",
                fontWeight: 600,
              }}
            >
              Yes, Add Anyway
            </button>
          </div>
        </Box>
      </Modal>
    </>
  );
};

export default CartCardViewLayout;
